/**
 * **ST-27c — a ClickUp task's custom fields, readable.**
 *
 * Raw Data's `custom_fields` cell is ClickUp's JSON verbatim — `"Duration": 1`,
 * option ids, user objects, epoch milliseconds — truncated to 18rem. Clicking it
 * opens this: one row per field, decoded to what ClickUp's own screen shows,
 * with the stored value beside any that were decoded so the decoding can always
 * be checked. Copy JSON copies the cell exactly as the export has it.
 *
 * A native `<dialog>`, like the views-per-day panel: Esc, focus trapping and an
 * inert page for free.
 */
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '../lib/api'
import { Button, Note, Skeleton } from './ui'

/** Longer stored values (user and attachment objects) are left to Copy JSON. */
const STORED_MAX = 60

export interface CustomFieldsTarget {
  taskId: string
  name: string | null
  /** The cell's value — what Copy JSON copies. */
  raw: string
}

export function CustomFieldsPanel({
  target,
  onClose,
}: {
  target: CustomFieldsTarget
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    // jsdom has no showModal; the attribute is what it understands.
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal()
    } else {
      dialog.setAttribute('open', '')
    }
    closeButton.current?.focus()
  }, [])

  const fields = useQuery({
    queryKey: ['clickup-task-fields', target.taskId],
    queryFn: () => api.clickupTaskFields(target.taskId),
  })

  const close = () => {
    ref.current?.close?.()
    onClose()
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(target.raw)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const title = fields.data?.name ?? target.name ?? target.taskId

  return (
    <dialog
      ref={ref}
      className="panel-dialog"
      aria-label={`Custom Fields: ${title}`}
      onClose={onClose}
      onClick={(event) => {
        // The backdrop is the dialog element itself.
        if (event.target === ref.current) close()
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs" style={{ color: 'var(--text-label)' }}>
            Custom Fields · ClickUp Task {target.taskId}
          </div>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h2>
        </div>
        <button
          ref={closeButton}
          type="button"
          className="lightbox-close-inline"
          aria-label="Close"
          onClick={close}
        >
          ×
        </button>
      </div>

      {fields.isLoading ? (
        <Skeleton rows={6} />
      ) : fields.error ? (
        <Note tone="error">{(fields.error as Error).message}</Note>
      ) : !fields.data?.fields.length ? (
        <Note>This Task Has No Custom Fields Filled In.</Note>
      ) : (
        <div className="panel-dialog-scroll mt-3">
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <caption className="sr-only">Custom fields of {title}</caption>
            <tbody>
              {fields.data.fields.map((field) => (
                <tr key={field.name} style={{ borderTop: '1px solid var(--border)' }}>
                  <th
                    scope="row"
                    className="py-1.5 pr-4 text-left align-top font-medium"
                    style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap', width: '1%' }}
                  >
                    {field.name}
                  </th>
                  <td className="py-1.5 align-top" style={{ color: 'var(--text-primary)' }}>
                    <span style={{ overflowWrap: 'anywhere' }}>
                      {field.value === null ? (
                        <span style={{ color: 'var(--text-label)' }}>—</span>
                      ) : /^https?:\/\//.test(field.value) ? (
                        <a href={field.value} target="_blank" rel="noreferrer noopener">
                          {field.value}
                        </a>
                      ) : (
                        field.value
                      )}
                    </span>
                    {field.decoded && field.stored.length <= STORED_MAX && (
                      // The stored code beside the decoded value, so a reader can
                      // check it against the export. A person or attachment is a
                      // whole JSON object — noise here, and in Copy JSON anyway.
                      <span
                        className="ml-2 font-mono text-xs"
                        style={{ color: 'var(--text-label)', overflowWrap: 'anywhere' }}
                      >
                        {field.stored}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        <Button variant="quiet" onClick={copy}>
          {copied ? 'Copied' : 'Copy JSON'}
        </Button>
        <Button variant="quiet" onClick={close}>
          Close
        </Button>
      </div>
    </dialog>
  )
}
